import React, { Fragment, useCallback, useEffect, useMemo, useRef } from "react";
import { ReactSVG } from "react-svg";

import starIcon from "../../assets/svg/solar--star-bold.svg";
import { triggerHapticFeedback } from "../../utils/hapticFeedback";

const cx = (...parts) => parts.filter(Boolean).join(" ");

export function getRatingTone(value) {
   const rounded = Math.round(Number(value));
   if (!Number.isFinite(rounded) || rounded <= 0) return 0;
   return Math.max(1, Math.min(5, rounded));
}

export default function RatingStarsRow({
   value = 0,
   previewValue = 0,
   max = 5,
   size = "md",
   interactive = false,
   multi = false,
   disabled = false,
   className = "",
   onChange,
   onPreviewChange,
}) {
   const lastSelectedStarRef = useRef(0);

   const selectedSet = useMemo(() => {
      if (!multi) return null;
      return new Set(
         (Array.isArray(value) ? value : [])
            .map((entry) => Number(entry))
            .filter(
               (entry) =>
                  Number.isFinite(entry) && entry >= 1 && entry <= max,
            ),
      );
   }, [max, multi, value]);

   const rating = Number(value);
   const safe = Number.isFinite(rating)
      ? Math.max(0, Math.min(max, rating))
      : 0;
   const preview = Number(previewValue);
   const activeCount =
      interactive && Number.isFinite(preview) && preview > 0
         ? preview
         : multi
           ? 0
           : safe;
   const tone = multi ? 0 : getRatingTone(activeCount);

   useEffect(() => {
      lastSelectedStarRef.current = safe;
   }, [safe]);

   const applyStarSelection = useCallback(
      (starValue) => {
         if (starValue <= 0 || disabled) return;

         if (multi) {
            const next = new Set(selectedSet);
            if (next.has(starValue)) next.delete(starValue);
            else next.add(starValue);
            triggerHapticFeedback();
            onChange?.(
               Array.from(next).sort((a, b) => a - b),
            );
            onPreviewChange?.(0);
            return;
         }

         if (lastSelectedStarRef.current !== starValue) {
            lastSelectedStarRef.current = starValue;
            triggerHapticFeedback();
            onChange?.(starValue);
         }

         onPreviewChange?.(starValue);
      },
      [disabled, multi, onChange, onPreviewChange, selectedSet],
   );

   const rootClassName = cx(
      "ratingStarsRow",
      `ratingStarsRow--${size}`,
      tone > 0 ? `is-tone-${tone}` : "",
      interactive ? "ratingStarsRow--interactive" : "",
      multi ? "ratingStarsRow--multi" : "",
      className,
   );

   return (
      <div
         className={rootClassName}
         onMouseLeave={
            interactive ? () => onPreviewChange?.(0) : undefined
         }
         role={interactive ? (multi ? "group" : "radiogroup") : undefined}
         aria-hidden={interactive ? undefined : true}
      >
         <div className="ratingStarsRow__buttons">
            {Array.from({ length: max }, (_, index) => {
               const starValue = index + 1;
               const isSelected = multi && selectedSet?.has(starValue);
               const isLastActive =
                  !multi &&
                  starValue === activeCount &&
                  activeCount > 0 &&
                  activeCount < max;
               const isFirstInactive =
                  !multi &&
                  starValue === activeCount + 1 &&
                  activeCount > 0 &&
                  activeCount < max;

               const starClass = multi
                  ? cx(
                       "ratingStarsRow__star",
                       `is-star-${starValue}`,
                       isSelected ? "is-selected" : "",
                       preview === starValue ? "is-preview" : "",
                    )
                  : cx(
                       "ratingStarsRow__star",
                       starValue < activeCount ? "is-done" : "",
                       starValue === activeCount && activeCount > 0
                          ? "is-current"
                          : "",
                       isLastActive ? "is-last-active" : "",
                       isFirstInactive ? "is-first-inactive" : "",
                    );
               const connectorClass = cx(
                  "ratingStarsRow__connector",
                  !multi &&
                     starValue === activeCount &&
                     activeCount > 0 &&
                     activeCount < max
                     ? "is-active"
                     : "",
               );

               const icon = (
                  <ReactSVG
                     src={starIcon}
                     className="ratingStarsRow__icon"
                     aria-hidden="true"
                  />
               );

               return (
                  <Fragment key={starValue}>
                     {interactive ? (
                        <button
                           type="button"
                           className={starClass}
                           data-no-haptic
                           onMouseEnter={() => onPreviewChange?.(starValue)}
                           onFocus={() => onPreviewChange?.(starValue)}
                           onBlur={() => onPreviewChange?.(0)}
                           onClick={() => {
                              applyStarSelection(starValue);
                              onPreviewChange?.(0);
                           }}
                           disabled={disabled}
                           aria-label={`${starValue} stele`}
                           aria-pressed={
                              multi ? Boolean(isSelected) : safe === starValue
                           }
                        >
                           {icon}
                        </button>
                     ) : (
                        <span className={starClass}>{icon}</span>
                     )}
                     {starValue < max && (
                        <span className={connectorClass} aria-hidden="true" />
                     )}
                  </Fragment>
               );
            })}
         </div>
      </div>
   );
}
